<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import ShopsAPI from 'dashboard/api/shops';
import ProductCategoriesAPI from 'dashboard/api/productCategories';
import Drawer from './Drawer.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Textarea from 'dashboard/components-next/textarea/TextArea.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';

const props = defineProps({
  shop: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['close', 'save']);
const { t } = useI18n();

const loading = ref(false);
const availableCategories = ref([]);
const loadingCategories = ref(false);

const formData = ref({
  name: '',
  slug: '',
  description: '',
  product_category_ids: [],
  active: true,
});

const isEditMode = computed(() => !!props.shop);
const drawerTitle = computed(() =>
  isEditMode.value
    ? t('WEBSITE.SHOPS.FORM.EDIT')
    : t('WEBSITE.SHOPS.FORM.NEW_SHOP')
);

const fullUrl = computed(() => {
  const slug = formData.value.slug || 'example';
  return `<domain>/shop/${slug}`;
});

// Génère automatiquement le slug à partir du nom
watch(
  () => formData.value.name,
  newName => {
    if (!isEditMode.value || !formData.value.slug) {
      formData.value.slug = newName
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
    }
  }
);

const fetchCategories = async () => {
  loadingCategories.value = true;
  try {
    const response = await ProductCategoriesAPI.get();
    availableCategories.value = response.data;
  } catch (error) {
    useAlert(t('WEBSITE.SHOPS.API.ERROR.FETCH_CATEGORIES_FAILED'));
  } finally {
    loadingCategories.value = false;
  }
};

onMounted(async () => {
  await fetchCategories();

  if (props.shop) {
    // Convertir les anciennes catégories (noms) en IDs si nécessaire
    let productCategoryIds = [];
    if (
      props.shop.product_category_ids &&
      Array.isArray(props.shop.product_category_ids)
    ) {
      productCategoryIds = props.shop.product_category_ids;
    } else if (props.shop.categories && Array.isArray(props.shop.categories)) {
      // Migration depuis l'ancien système : trouver les IDs correspondants
      const categoryNames = props.shop.categories;
      productCategoryIds = availableCategories.value
        .filter(cat => categoryNames.includes(cat.name))
        .map(cat => cat.id);
    }

    formData.value = {
      name: props.shop.name || '',
      slug: props.shop.slug || '',
      description: props.shop.description || '',
      product_category_ids: productCategoryIds,
      active: props.shop.active !== undefined ? props.shop.active : true,
    };
  }
});

const toggleCategory = categoryId => {
  const index = formData.value.product_category_ids.indexOf(categoryId);
  if (index === -1) {
    formData.value.product_category_ids.push(categoryId);
  } else {
    formData.value.product_category_ids.splice(index, 1);
  }
};

const isCategorySelected = categoryId => {
  return formData.value.product_category_ids.includes(categoryId);
};

const handleSubmit = async () => {
  if (!formData.value.name) {
    useAlert(t('WEBSITE.SHOPS.API.ERROR.NAME_REQUIRED'));
    return;
  }

  if (!formData.value.slug) {
    useAlert(t('WEBSITE.SHOPS.API.ERROR.SLUG_REQUIRED'));
    return;
  }

  loading.value = true;
  try {
    const payload = {
      shop: {
        name: formData.value.name,
        slug: formData.value.slug,
        description: formData.value.description,
        active: formData.value.active,
        product_category_ids: formData.value.product_category_ids,
      },
    };

    if (isEditMode.value) {
      await ShopsAPI.update(props.shop.id, payload);
      useAlert(t('WEBSITE.SHOPS.API.SUCCESS.SHOP_UPDATED'));
    } else {
      await ShopsAPI.create(payload);
      useAlert(t('WEBSITE.SHOPS.API.SUCCESS.SHOP_CREATED'));
    }

    emit('save');
  } catch (error) {
    if (error.response?.data?.errors) {
      const errorMessage = Object.values(error.response.data.errors)
        .flat()
        .join(', ');
      useAlert(errorMessage);
    } else {
      useAlert(
        isEditMode.value
          ? t('WEBSITE.SHOPS.API.ERROR.UPDATE_FAILED')
          : t('WEBSITE.SHOPS.API.ERROR.CREATE_FAILED')
      );
    }
  } finally {
    loading.value = false;
  }
};

const handleCancel = () => {
  emit('close');
};
</script>

<template>
  <Drawer show size="md" @close="handleCancel">
    <!-- Header -->
    <div
      class="flex items-center justify-between px-6 py-4 border-b border-n-weak"
    >
      <h2 class="text-lg font-semibold text-n-slate-12">
        {{ drawerTitle }}
      </h2>
      <button
        class="flex items-center justify-center w-8 h-8 rounded-lg hover:bg-n-alpha-2 text-n-slate-11 transition-colors"
        @click="handleCancel"
      >
        <span class="i-lucide-x size-5" />
      </button>
    </div>

    <!-- Content -->
    <div class="flex-1 overflow-y-auto px-6 py-6">
      <form class="space-y-6" @submit.prevent="handleSubmit">
        <!-- Nom du shop -->
        <div>
          <label
            class="block text-sm font-medium text-n-slate-12 mb-1.5"
            for="shop-name"
          >
            {{ t('WEBSITE.SHOPS.FORM.NAME') }}
            <span class="text-red-500" aria-hidden="true">*</span>
          </label>
          <Input
            id="shop-name"
            v-model="formData.name"
            :placeholder="t('WEBSITE.SHOPS.FORM.NAME_PLACEHOLDER')"
            required
          />
        </div>

        <!-- Slug (URL) -->
        <div>
          <label
            class="block text-sm font-medium text-n-slate-12 mb-1.5"
            for="shop-slug"
          >
            {{ t('WEBSITE.SHOPS.FORM.SLUG') }}
            <span class="text-red-500" aria-hidden="true">*</span>
          </label>
          <Input
            id="shop-slug"
            v-model="formData.slug"
            :placeholder="t('WEBSITE.SHOPS.FORM.SLUG_PLACEHOLDER')"
            required
          />
          <p class="mt-1 text-xs text-n-slate-10">
            {{ t('WEBSITE.SHOPS.FORM.SLUG_HELP') }}
          </p>
        </div>

        <!-- Aperçu de l'URL -->
        <div class="p-3 rounded-lg bg-n-alpha-1 border border-n-weak">
          <p class="text-xs font-medium text-n-slate-11 mb-1">
            {{ t('WEBSITE.SHOPS.FORM.URL_PREVIEW') }}
          </p>
          <code class="text-sm text-n-blue-11 break-all">{{ fullUrl }}</code>
        </div>

        <!-- Description -->
        <div>
          <label
            class="block text-sm font-medium text-n-slate-12 mb-1.5"
            for="shop-description"
          >
            {{ t('WEBSITE.SHOPS.FORM.DESCRIPTION') }}
          </label>
          <Textarea
            id="shop-description"
            v-model="formData.description"
            :placeholder="t('WEBSITE.SHOPS.FORM.DESCRIPTION_PLACEHOLDER')"
            :rows="3"
          />
        </div>

        <!-- Catégories de produits -->
        <div>
          <label class="block text-sm font-medium text-n-slate-12 mb-2">
            {{ t('WEBSITE.SHOPS.FORM.CATEGORIES') }}
          </label>
          <p class="text-xs text-n-slate-10 mb-3">
            {{ t('WEBSITE.SHOPS.FORM.CATEGORIES_HELP') }}
          </p>

          <div v-if="loadingCategories" class="text-sm text-n-slate-10">
            {{ t('WEBSITE.SHOPS.FORM.LOADING_CATEGORIES') }}
          </div>

          <div
            v-else-if="availableCategories.length === 0"
            class="text-sm text-n-slate-10"
          >
            {{ t('WEBSITE.SHOPS.FORM.NO_CATEGORIES') }}
          </div>

          <div v-else class="space-y-4">
            <div
              v-for="category in availableCategories"
              :key="category.id"
              class="border border-n-weak rounded-lg p-3 hover:border-n-blue-6 transition-colors"
            >
              <!-- Checkbox et nom de catégorie -->
              <div class="flex items-center">
                <input
                  :id="`category-${category.id}`"
                  type="checkbox"
                  :value="category.id"
                  :checked="isCategorySelected(category.id)"
                  class="w-4 h-4 text-n-blue-9 bg-n-solid-1 border-n-weak rounded focus:ring-n-blue-9 focus:ring-2"
                  @change="toggleCategory(category.id)"
                />
                <label
                  :for="`category-${category.id}`"
                  class="ml-2 text-sm font-medium text-n-slate-12 cursor-pointer flex items-center gap-2"
                >
                  <img
                    v-if="category.logo"
                    :src="category.logo.url"
                    :alt="category.name"
                    class="w-5 h-5 rounded object-cover"
                  />
                  <span v-else class="i-lucide-tag size-4 text-n-slate-10" />
                  {{ category.name }}
                </label>
              </div>
            </div>
          </div>
        </div>

        <!-- Statut actif/inactif -->
        <div class="flex items-center">
          <Checkbox id="shop-active" v-model="formData.active" />
          <label
            for="shop-active"
            class="ml-2 text-sm text-n-slate-12 cursor-pointer"
          >
            {{ t('WEBSITE.SHOPS.FORM.ACTIVE') }}
          </label>
        </div>
      </form>
    </div>

    <!-- Footer -->
    <div
      class="flex items-center justify-end gap-3 px-6 py-4 border-t border-n-weak"
    >
      <Button
        :label="t('WEBSITE.SHOPS.FORM.CANCEL')"
        variant="outline"
        color="slate"
        @click="handleCancel"
      />
      <Button
        :label="
          isEditMode
            ? t('WEBSITE.SHOPS.FORM.SAVE')
            : t('WEBSITE.SHOPS.FORM.CREATE')
        "
        :loading="loading"
        @click="handleSubmit"
      />
    </div>
  </Drawer>
</template>
